import { useState, useEffect } from 'react'
import { BottomSheet } from '@/shared/ui/bottom-sheet'
import { Button } from '@/shared/ui/button'
import { useTelegram } from '@/shared/lib/telegram'
import { useCreateDrinkHistoryEntry } from '@/features/drink-history/api/use-drink-history'
import { DiaryForm, type DiaryFormData } from '@/widgets/diary-form'
import type { Tea } from '@/entities/product/model/types'

export interface QuickAddSheetProps {
  isOpen: boolean
  onClose: () => void
  tea?: Tea | null
  teaId?: string
  teaName?: string
  teaCategory?: string
  photoUrl?: string  // Фото чая из каталога (для отображения)
  defaultBrewingParams?: {
    temperature?: number
    time?: number
    teaAmount?: number
    waterAmount?: number
  }
  onSuccess?: () => void
}

/**
 * Виджет: Bottom Sheet для быстрого добавления записи в дневник
 * 
 * Использует унифицированную DiaryForm.
 */
export function QuickAddSheet({
  isOpen,
  onClose,
  tea,
  teaId,
  teaName,
  teaCategory,
  photoUrl,
  defaultBrewingParams,
  onSuccess,
}: QuickAddSheetProps) {
  const { hapticFeedback } = useTelegram()
  const createEntry = useCreateDrinkHistoryEntry()
  
  const [formData, setFormData] = useState<DiaryFormData>({
    rating: null,
    mood: null,
    brewingMethod: null,
    notes: '',
    userPhotoUrl: null,
    brewingParams: {},
  })
  
  // Сброс формы при открытии
  useEffect(() => {
    if (isOpen) {
      setFormData({
        rating: null,
        mood: null,
        brewingMethod: null,
        notes: '',
        userPhotoUrl: null,
        brewingParams: {
          temperature: defaultBrewingParams?.temperature,
          brewingTime: defaultBrewingParams?.time,
          teaAmount: defaultBrewingParams?.teaAmount,
          waterAmount: defaultBrewingParams?.waterAmount,
        },
      })
    }
  }, [isOpen, defaultBrewingParams])
  
  const effectiveTeaId = tea?.id || teaId || ''
  const effectiveTeaName = tea?.name || teaName || 'Чай'
  const effectiveTeaCategory = tea?.categoryName || teaCategory
  const effectivePhotoUrl = tea?.images?.[0]?.url || photoUrl
  
  const handleSave = async () => {
    if (!effectiveTeaId) return
    
    hapticFeedback.impactOccurred('medium')
    
    try {
      // Формируем параметры заваривания
      const brewingParams = (
        formData.brewingParams.temperature ||
        formData.brewingParams.brewingTime ||
        formData.brewingParams.teaAmount ||
        formData.brewingParams.waterAmount
      ) ? {
        temperature: formData.brewingParams.temperature,
        time: formData.brewingParams.brewingTime,
        teaAmount: formData.brewingParams.teaAmount,
        waterAmount: formData.brewingParams.waterAmount,
      } : undefined

      // Бэкенд получит photoUrl из product_media по teaId
      // userPhotoUrl - загруженное пользователем фото (имеет приоритет)
      await createEntry.mutateAsync({
        teaId: effectiveTeaId,
        rating: formData.rating !== null ? formData.rating : undefined,
        mood: formData.mood || undefined,
        brewingMethod: formData.brewingMethod || undefined,
        notes: formData.notes.trim() || undefined,
        brewingParams,
        userPhotoUrl: formData.userPhotoUrl || undefined,
      })
      
      hapticFeedback.notificationOccurred('success')
      onSuccess?.()
      onClose()
    } catch (error) {
      hapticFeedback.notificationOccurred('error')
      console.error('Ошибка при сохранении:', error)
    }
  }
  
  const handleChange = (updates: Partial<DiaryFormData>) => {
    setFormData(prev => ({ ...prev, ...updates }))
  }
  
  return (
    <BottomSheet
      isOpen={isOpen}
      onClose={onClose}
      title="Записать в дневник"
      height={85}
      onHapticFeedback={(type) => {
        if (type) hapticFeedback.impactOccurred(type)
      }}
    >
      <div className="flex flex-col h-full">
        <div className="flex-1 overflow-y-auto px-6 py-4">
          <DiaryForm
            data={formData}
            onChange={handleChange}
            teaInfo={{
              name: effectiveTeaName,
              category: effectiveTeaCategory,
              imageUrl: effectivePhotoUrl,
            }}
            showPhoto={true}
            compact={true}
          />
        </div>
        
        {/* Кнопки */}
        <div className="flex-shrink-0 px-6 py-4 border-t border-silk dark:border-dark-border bg-paper dark:bg-dark-bg">
          <div className="flex gap-3">
            <Button
              type="button"
              variant="secondary"
              fullWidth
              onClick={() => {
                hapticFeedback.impactOccurred('light')
                onClose()
              }}
            >
              Отмена
            </Button>
            <Button
              type="button"
              variant="primary"
              fullWidth
              loading={createEntry.isPending}
              onClick={handleSave}
              disabled={!effectiveTeaId}
            >
              Записать
            </Button>
          </div>
        </div>
      </div>
    </BottomSheet>
  )
}

export default QuickAddSheet
